import { 
  collection, 
  doc, 
  addDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  query, 
  where
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage } from "../utils/firebase";
import { Property } from "../types";

const PROPERTIES_COLLECTION = "properties";

// Get all properties
export const getAllProperties = async (): Promise<Property[]> => {
  try {
    const propertiesCollection = collection(db, PROPERTIES_COLLECTION);
    const querySnapshot = await getDocs(propertiesCollection);
    
    const properties: Property[] = [];
    querySnapshot.forEach((doc) => {
      const property = { id: doc.id, ...doc.data() } as Property;
      properties.push(property);
    });
    
    return properties;
  } catch (error) {
    console.error("Error getting properties:", error);
    throw error;
  }
};

// Get properties by availability
export const getPropertiesByAvailability = async (available: boolean): Promise<Property[]> => {
  try {
    const propertiesCollection = collection(db, PROPERTIES_COLLECTION);
    const q = query(propertiesCollection, where("available", "==", available));
    const querySnapshot = await getDocs(q);
    
    const properties: Property[] = [];
    querySnapshot.forEach((doc) => {
      const property = { id: doc.id, ...doc.data() } as Property;
      properties.push(property);
    });
    
    return properties;
  } catch (error) {
    console.error(`Error getting ${available ? "available" : "unavailable"} properties:`, error);
    throw error;
  }
};

// Get a property by ID
export const getPropertyById = async (id: string): Promise<Property | null> => {
  try {
    const propertyDoc = doc(db, PROPERTIES_COLLECTION, id);
    const docSnapshot = await getDoc(propertyDoc);
    
    if (docSnapshot.exists()) {
      return { id: docSnapshot.id, ...docSnapshot.data() } as Property;
    }
    
    return null;
  } catch (error) {
    console.error(`Error getting property with ID ${id}:`, error);
    throw error;
  }
};

// Add a new property
export const addProperty = async (property: Omit<Property, 'id'>): Promise<string> => {
  try {
    const propertiesCollection = collection(db, PROPERTIES_COLLECTION);
    const docRef = await addDoc(propertiesCollection, property);
    return docRef.id;
  } catch (error) {
    console.error("Error adding property:", error);
    throw error;
  }
};

// Update a property
export const updateProperty = async (id: string, propertyUpdates: Partial<Property>): Promise<void> => {
  try {
    const propertyDoc = doc(db, PROPERTIES_COLLECTION, id);
    await updateDoc(propertyDoc, propertyUpdates);
  } catch (error) {
    console.error(`Error updating property with ID ${id}:`, error);
    throw error;
  }
};

// Delete a property
export const deleteProperty = async (id: string): Promise<void> => {
  try {
    const propertyDoc = doc(db, PROPERTIES_COLLECTION, id);
    await deleteDoc(propertyDoc);
  } catch (error) {
    console.error(`Error deleting property with ID ${id}:`, error);
    throw error;
  }
};

// Upload property image and get download URL
export const uploadPropertyImage = async (file: File, propertyId: string, index: number): Promise<string> => {
  try {
    const imageRef = ref(storage, `properties/${propertyId}/images/${index}_${file.name}`);
    await uploadBytes(imageRef, file);
    const downloadURL = await getDownloadURL(imageRef);
    return downloadURL;
  } catch (error) {
    console.error("Error uploading image:", error);
    throw error;
  }
};

// Delete property image
export const deletePropertyImage = async (imageUrl: string): Promise<void> => {
  try {
    const imageRef = ref(storage, imageUrl);
    await deleteObject(imageRef);
  } catch (error) {
    console.error("Error deleting image:", error);
    throw error;
  }
};